import { Fragment, useCallback, useImperativeHandle, useState } from 'react';
import { batch, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import { Label, Switch, WithTooltip } from 'basic-components';
import { Info, Unlock } from 'basic-components/icons';
import { DataScienceEnums } from 'dashboard-services/api/dataScience';
import { CommonService } from 'dashboard-services/services';

import { builderPipelineCreatorActions } from 'actions/modules/datascience';
import { codeEditorActions } from 'actions/modules/tools';

import { getProvider } from '../ProviderService';
import CreateFile from './CreateFile';
import Download from './Download';
import Open from './Open';
import Preview, { isFileTypeSupportedForPreview } from './Preview';
import PreviewExpression from './PreviewExpression';
import TextAreaModal from './TextAreaModal';

import { When } from 'react-if';

import classNames from 'classnames';
import './Header.scss';

const Header = ({
  dependsOnParam = undefined,
  optional,
  value = "",
  valueType,
  description = undefined,
  name,
  withUnlock = false,
  onUnlockClick = undefined,
  taskName,
  onChange = undefined,
  disabled = false,
  readableName = undefined,
  expression = false,
  getAllParams = undefined,
  isInModal = false,
  withProvider = false,
  ref,
  ...props
}) => {
  const dispatch = useDispatch(),
        [textAreaOpen, setTextAreaOpen] = useState(false),
        provider = getProvider({ dependsOnParam, value, withProvider }),
        [isValid, setIsValid] = useState(true),
        onChangeBoolean = useCallback(() => {
          if(disabled) {
            return;
          }
          const valueLocal = value === true || value === "true"
          if(onChange) {
            onChange(!valueLocal, name)
          }
        }, [disabled, value, onChange, name]),
        onChangeBooleanPause = useCallback(() => {
          if(disabled) {
            return;
          }
          const valueLocal = value === true || value === "true"
          if(onChange) {
            onChange(!valueLocal, name)
          }
          dispatch(builderPipelineCreatorActions.changeSelectedTaskProp(!valueLocal, "isPaused"))
        }, [disabled, value, onChange, dispatch, name]),
        onChangeStandard = useCallback(value => {
          if(disabled) {
            return;
          }
          if(onChange) {
            onChange(value, name)
          }
        }, [disabled, onChange, name]),
        onChangeJson = useCallback(val => {
          if(disabled) {
            return;
          }
          onChangeStandard(CommonService.isJson(val) ? JSON.parse(val) : val)
        }, [disabled, onChangeStandard]),
        openTextArea = useCallback(() => setTextAreaOpen(true), []),
        closeTextArea = useCallback(() => setTextAreaOpen(false), []),
        openValueCodeEditor = useCallback(valueModeType => {
          batch(() => {
            dispatch(codeEditorActions.changeValue(undefined, 'id'))
            dispatch(codeEditorActions.changeValue(true, 'open'))
            dispatch(codeEditorActions.changeValue(!disabled, 'editable'))
            dispatch(codeEditorActions.changeValue("json", 'extension'))
            dispatch(codeEditorActions.changeValue(CommonService.isJson(value, true) ? JSON.parse(value) : value, 'defaultValue'))
            dispatch(codeEditorActions.changeValue(true, 'valueMode'))
            dispatch(codeEditorActions.changeValue(valueModeType, 'valueModeType'))
            dispatch(codeEditorActions.changeValue(onChangeJson, 'valueCallback'))
          })
        }, [dispatch, disabled, value, onChangeJson]),
        labelOnClick = useCallback(() => {
          if(valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.TEXT_AREA) {
            return openTextArea() 
          } else if(valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.JSON) {
            return openValueCodeEditor("json")
          }
          return undefined;
        }, [openTextArea, valueType, openValueCodeEditor])

  useImperativeHandle(ref, () => ({
    validateForForm: () => {
      if(optional) {
        return Promise.resolve(true)
      }
      if(valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.TEXT_AREA) {
        const value = typeof value === "object" ? JSON.stringify(value) : value?.toString()
        const isValid = value !== ""
        setIsValid(isValid)
      } else if(valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.JSON) {
        const value = CommonService.isJson(value, true) ? JSON.parse(value) : value
        const isValid = !!value && JSON.stringify(value) !== "" && JSON.stringify(value) !== "[]" && JSON.stringify(value) !== "{}"
        setIsValid(isValid)
      }
      return Promise.resolve(true)
    },
    isValid: () => optional ? true : isValid
  }))

  return (
    <div className={classNames("data-science__pipeline-creator__body__options__resolver__header", { "data-science__pipeline-creator__body__options__resolver__header--with-bolean": valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.BOOLEAN || valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.BOOLEAN_PAUSE }, { "data-science__pipeline-creator__body__options__resolver__header--label-clickable": valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.TEXT_AREA || valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.JSON })}>
      <Label
          onClick={labelOnClick}
          required={!optional && valueType !== DataScienceEnums.TASK_PARAM_VALUE_TYPE.BOOLEAN && valueType !== DataScienceEnums.TASK_PARAM_VALUE_TYPE.BOOLEAN_PAUSE}
          size="big"
      >
        {readableName || (name !== "defaultValue" && name !== "value" ? name : "Click here")}
      </Label>
      <When condition={valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.BOOLEAN}>
        {() => (
          <Fragment>
            <Switch
                {...props}
                disabled={disabled}
                on={value === "true" || value === true}
                onChange={onChangeBoolean}
                ref={ref}
            />
          </Fragment>
        )}
      </When>
      <When condition={valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.BOOLEAN_PAUSE}>
        {() => (
          <Switch
              {...props}
              disabled={disabled}
              on={value === "true" || value === true}
              onChange={onChangeBooleanPause}
              ref={ref}
          />
        )}
      </When>
      <div className="data-science__pipeline-creator__body__options__resolver__header__controls">
        <When condition={value !== "" && value !== " " && valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.DATALAKE_FILE && isFileTypeSupportedForPreview(value?.name ? String(value?.name) : "") && !isInModal}>
          {() => (
            <Preview 
                {...props}
                name={readableName || name}
                onChange={onChangeStandard}
                provider={provider}
                value={value}
                valueType={valueType}
            />
          )}
        </When>
        <When condition={(value === "" || value === " ") && valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.DATALAKE_FILE && !isInModal}>
          {() => (
            <CreateFile
                {...props}
                onChange={onChangeStandard}
            />
          )}
        </When>
        <When condition={value !== "" && value !== " " && expression && getAllParams}>
          {() => (
            <PreviewExpression
                getAllParams={getAllParams}
                name={name}
                value={value}
            />
          )}
        </When>
        <When condition={value !== "" && value !== " " && valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.DATALAKE_FILE}>
          {() => (
            <Download
                provider={provider}
                value={value}
            />
          )}
        </When>
        <When condition={value !== "" && value !== " " && (((valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.GROUP || valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.GROUP_READ) && !String(value).startsWith("{ID:")) || valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.DATA_PREP_PARAM || valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.SYMBOL || valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.PIPELINE || valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.DATALAKE_FILE)}>
          {() => (
            <Open
                provider={provider}
                value={value}
                valueType={valueType}
            />
          )}
        </When>
        <When condition={withUnlock}>
          {() => (
            <WithTooltip
                data-place="left"
                text="Click to remove constant and enable connection"
            >
              <div 
                  className={classNames(
                    "data-science__pipeline-creator__body__options__resolver__header__controls__control",
                    "data-science__pipeline-creator__body__options__resolver__header__controls__control--unlock",
                    "data-science__pipeline-creator__body__options__resolver__header__controls__control--unlock--is-disabled"
                  )}
                  onClick={onUnlockClick}
              >
                  <Unlock/>
              </div>
            </WithTooltip>
          )}
        </When>
        <When condition={description}>
          {() => (
            <WithTooltip
                data-place="left"
                text={description}
            >
              <div className="data-science__pipeline-creator__body__options__resolver__header__controls__control">
                <Info
                    color="blue-bright"
                    height={16}
                    width={16}
                />
              </div>
            </WithTooltip>
          )}
        </When>
      </div>
      <When condition={textAreaOpen}>
        {() => (
          <TextAreaModal
              {...props}
              close={closeTextArea}
              defaultValue={typeof value === "object" ? JSON.stringify(value) : value?.toString()}
              disabled={disabled}
              onSubmit={onChangeStandard}
              optionalText={`${taskName}: ${readableName || name}`}
              ref={ref}
          />
        )}
      </When>
    </div>
  )
}

Header.displayName = "ValueTypeResolverHeader"


Header.propTypes = {
  dependsOnParam: PropTypes.object,
  description: PropTypes.string,
  disabled: PropTypes.bool,
  expression: PropTypes.bool,
  getAllParams: PropTypes.func,
  isInModal: PropTypes.bool,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  onUnlockClick: PropTypes.func,
  optional: PropTypes.bool.isRequired,
  readableName: PropTypes.string,
  taskName: PropTypes.string.isRequired,
  value: PropTypes.any,
  valueType: PropTypes.string.isRequired,
  withProvider: PropTypes.bool,
  withUnlock: PropTypes.bool
}

export default Header;
